import {
  DatePicker,
  DatePickerProps,
  LocalizationProvider,
} from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { dateFormat } from '@utils/format';
import moment from 'moment';
import { getColor } from '@assets/theme';
import { useCallback } from 'react';
import { debounce } from '@mui/material';

/**
 * Props for the ControlledDatepicker component.
 * Extends DatePickerProps from MUI, omitting 'value' and 'onChange'.
 */
interface ControlledDatepickerProps extends Omit<
  DatePickerProps<moment.Moment>,
  'value' | 'onChange'
> {
  /** The currently selected date */
  selectedDate: moment.Moment | null;
  /** Function to update the selected date */
  setSelectedDate: React.Dispatch<React.SetStateAction<moment.Moment | null>>;
  /** Optional callback function when the date changes */
  onChange?: (date: moment.Moment | null) => void;
  /** Label for the date picker */
  label: string;
  clearable?: boolean;
  isDataTable?: boolean;
}

/**
 * A controlled date picker component using MUI's DatePicker.
 * This component provides a customized date picker with debounced onChange.
 *
 * @param {ControlledDatepickerProps} props - The props for the component
 * @returns {React.ReactElement} The rendered ControlledDatepicker component
 */
const ControlledDatepicker: React.FC<ControlledDatepickerProps> = ({
  selectedDate,
  setSelectedDate,
  onChange,
  label,
  clearable = true,
  isDataTable = true,
  ...etc
}) => {
  const { textColor } = getColor();

  /**
   * Debounced onChange handler to prevent rapid updates
   */
  const debouncedOnChange = useCallback(
    debounce((newValue: any) => {
      setSelectedDate(newValue);
      onChange?.(newValue);
    }, 200),
    [],
  );

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <DatePicker
        {...etc}
        format={dateFormat}
        localeText={{
          fieldMonthPlaceholder: () => 'MMM',
        }}
        label={label}
        value={selectedDate}
        onChange={debouncedOnChange}
        slotProps={{
          field: {
            clearable: clearable,
          },
          textField: {
            size: 'small',
            InputLabelProps: { shrink: true },
            placeholder: dateFormat,
          },
          layout: {
            sx: {
              '& .MuiPickersCalendarHeader-switchViewButton, .MuiPickersArrowSwitcher-root button':
                {
                  border: 0,
                  color: '#161C248F',
                  bgcolor: 'transparent',
                },
              '.MuiDialogActions-root button': {
                width: '100%',
                bgcolor: '#DFE3E8',
                color: textColor,
              },
            },
          },
        }}
        sx={{
          ...(isDataTable && {
            width: { xs: '100%', sm: 'auto' },
            '& .MuiOutlinedInput-root': {
              '& > fieldset': {
                // Changed the borderColor to default: if needed uncomment the borderColor
                // borderColor: '#EEE',
              },
            },
            '& .MuiInputAdornment-root button': {
              border: 0,
              color: '#161C248F',
              bgcolor: 'transparent',
            },
            '& .MuiInputBase-root': { border: '0!important' },
            input: {
              // Changed the size to default: if needed uncomment the py
              pl: 0,
              // py: '7.5px',
            },
          }),
          ...(etc.sx ?? {}),
        }}
      />
    </LocalizationProvider>
  );
};

export default ControlledDatepicker;
